import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import JsonViewer from "./components/JsonViewer";
import ConsoleQuery from "./components/ConsoleQuery";
import CodeEditor from "./components/CodeEditor";
import DebugPanel from "./components/DebugPanel";
import Icon from "../../components/AppIcon";
import Footer from "components/ui/Footer";

const AboutMe = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(() => {
    try {
      return !localStorage.getItem("hasVisitedAboutMe");
    } catch (e) {
      return true;
    }
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Simulate loading only if it's the first time
    let loadTimer;
    if (isLoading) {
      loadTimer = setTimeout(() => {
        setIsLoading(false);
        try {
          localStorage.setItem("hasVisitedAboutMe", "true");
        } catch (e) {
          console.error("Failed to set localStorage key", e);
        }
      }, 1500);
    }

    return () => {
      clearInterval(timer);
      if (loadTimer) {
        clearTimeout(loadTimer);
      }
    };
  }, [isLoading]);

  const developerData = {
    name: "Kahnu Charan Swain",
    title: "Backend Developer (Frontend Curious)",
    location: "Somewhere debugging CSS",
    status: "Learning frontend one Stack Overflow answer at a time",
    email: "kahnu@developernoob.com",
    phone: "+91 (6370) - Use brute-force",
    website: "https://developernoob.com",
    summary: `Backend developer with 4+ years of experience building scalable systems.\nCurrently expanding into frontend development because someone said\n"How hard can CSS be?" (Narrator: It was very hard)`,
    experience: [
      {
        company: "HyScaler",
        position: "Software Development Engineer 1",
        duration: "Sep/2021 - Present",
        location: "Bhubaneswar, Odisha, India (Office)",
        responsibilities: [
          "Built microservices that actually work (most of the time)",
          "Optimized database queries until they screamed for mercy",
          "Mentored junior developers in the art of meaningful variable names",
          "Survived 50+ production deployments on Friday afternoons",
        ],
        achievements: [
          "Reduced API response time by 60% through caching strategies",
          "Led migration of monolith to microservices (only broke prod twice)",
          "Implemented SSO that never forgot a password",
        ],
        technologies: [
          "WordPress",
          "NodeJS",
          "PostgreSQL",
          "Docker",
          "AWS",
          "Python",
          "Django",
          "React",
          "Next.js",
          "NestJS",
          "Stripe API",
        ],
      },
    ],
    skills: {
      backend: {
        confidence: 95,
        years: 4,
        languages: ["PHP", "JavaScript", "Python"],
        frameworks: ["WordPress", "NodeJS", "Express", "Django"],
        databases: ["MySQL", "PostgreSQL", "MongoDB"],
        tools: ["Docker", "AWS"],
      },
      frontend: {
        confidence: "improving daily",
        tears_shed: "countless",
        languages: ["JavaScript", "HTML", "CSS (the nemesis)"],
        frameworks: ["React", "Next.js", "Vue.js (learning)", "Tailwind CSS"],
        status: "Can center a div (sometimes)",
        recent_victories: [
          "Made a responsive navbar without Bootstrap",
          "Used flexbox correctly on first try (once)",
          "Understood CSS Grid (briefly)",
        ],
      },
      devops: {
        confidence: 85,
        specialties: ["CI/CD", "Containerization", "Cloud Architecture"],
        cloud_providers: ["AWS", "Google Cloud", "DigitalOcean", "Linode"],
        monitoring: ["Prometheus", "Grafana", "ELK Stack"],
      },
    },
    education: {
      degree: "Master of Computer Applications (MCA)",
      university: "Ravenshaw University (Cuttack)",
      graduation: "2021",
      gpa: "7.0/10.0",
      relevant_courses: [
        "Data Structures & Algorithms",
        "Database Systems",
        "Software Engineering",
        "Computer Networks",
        "Web Development",
      ],
    },
    projects: [
      {
        name: "DeveloperNoob Portfolio",
        description:
          "This very website - a satirical take on developer portfolios\nthat accidentally became a real showcase of skills",
        technologies: ["React", "Tailwind CSS", "Framer Motion", "Tears"],
        github: "https://github.com/kahnu044/repo-is-private",
        status: "Work in progress (like my CSS skills)",
      },
      {
        name: "Doctegrity",
        description:
          "Where your symptoms meet their match, and waiting rooms are optional. Get doctor advice without awkward small talk or mysterious beeping machines!",
        technologies: [
          "Sails.js",
          "Next.js",
          "React.Js",
          "React Native",
          "MongoDB",
          "AWS",
        ],
        github: "https://github.com/kahnu044/there-is-no-such-repo",
        status: "Production ready (mostly)",
      },
    ],
    interests: [
      "System architecture",
      "Performance optimization",
      "Learning frontend (the eternal struggle)",
      "Coffee brewing (essential for debugging)",
      "Open source contributions",
      "Teaching others to code",
    ],
    fun_facts: [
      "Can write a REST API faster than I can style a button",
      "Has read 'You Don't Know JS' series twice",
      "Believes in semantic HTML (even if my CSS disagrees)",
      "Dreams in SQL queries",
      "Once fixed a production bug during a wedding ceremony",
    ],
  };

  const debugData = {
    achievements: [
      {
        title: "System Architecture Excellence",
        description:
          "Designed and implemented a microservices architecture serving 1M+ daily requests with 99.9% uptime",
        date: "2023",
        impact: "40% reduction in infrastructure costs",
      },
      {
        title: "Open Source Contributor",
        description:
          "Contributed to 15+ open source projects with over 500 GitHub stars earned",
        date: "2019-2024",
        impact: "Community recognition and networking",
      },
      {
        title: "Technical Leadership",
        description:
          "Led a team of 8 developers through successful product launch and scaling phase",
        date: "2022",
        impact: "Team productivity increased by 35%",
      },
    ],
    certifications: [
      {
        name: "Certified CSS Div Centerer",
        issuer: "Trial, Error & Stack Overflow",
        date: "After 3 Hours",
      },
      {
        name: "Professional Googler & Copy-Paster",
        issuer: "The School of MDN Web Docs",
        date: "Daily",
      },
      {
        name: "Master of 'It Works on My Machine'",
        issuer: "Localhost Certification Board",
        date: "Lifetime",
      },
    ],
    metrics: [
      {
        label: "Years Experience",
        value: "4+",
        trend: "+1 yearly",
      },
      {
        label: "Projects Completed",
        value: "50+",
        trend: "+13 this year",
      },
      {
        label: "Code Reviews",
        value: "1,200+",
        trend: "+50 monthly",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>AboutMe | DeveloperNoob</title>
        <meta
          name="description"
          content="A backend developer's journey into frontend territory with debugging tools and code editor interface."
        />
        <meta
          name="keywords"
          content="developer resume, JSON format, backend developer, frontend learning, interactive resume"
        />
      </Helmet>

      <Header />

      {isLoading ? (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <div className="font-source text-primary">
              <div className="animate-pulse">Loading about-me.json...</div>
              <div className="text-sm text-muted-foreground mt-2">
                Parsing personal data and debugging life choices
              </div>
            </div>
          </div>
        </div>
      ) : (
        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Hero Section */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
                <Icon name="FileText" size={16} className="text-primary" />
                <span className="text-sm font-source text-primary">
                  about-me.json
                </span>
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              </div>

              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                A Noob backend developer's journey into frontend territory.
                <br />
                <span className="text-sm font-source">
                  Warning: May contain traces of imposter syndrome and CSS
                  confusion
                </span>
              </p>

              <div className="flex items-center justify-between space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <Icon name="GitBranch" size={16} />
                  <span>Version 0.4.4</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                  <span>Status: Learning</span>
                </div>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid lg:grid-cols-3 gap-8">
              {/* JSON Viewer - Main Content */}
              <div className="lg:col-span-2 space-y-8">
                <CodeEditor title="about-me.json" showLineNumbers>
                  <JsonViewer data={developerData} />
                </CodeEditor>

                {/* Debug Panel */}
                <DebugPanel debugData={debugData} />
              </div>

              {/* Interactive Console - Sidebar */}
              <div className="space-y-8">
                <ConsoleQuery data={developerData} />

                {/* Quick Stats */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
                    <Icon name="Activity" size={20} />
                    <span>Quick Stats</span>
                  </h3>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        Backend Confidence
                      </span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="w-[95%] h-full bg-success"></div>
                        </div>
                        <span className="text-sm font-source text-success">
                          95%
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        Frontend Progress
                      </span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="w-[60%] h-full bg-warning animate-pulse"></div>
                        </div>
                        <span className="text-sm font-source text-warning">
                          Learning...
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        Coffee Dependency
                      </span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="w-full h-full bg-destructive"></div>
                        </div>
                        <span className="text-sm font-source text-destructive">
                          MAX
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Easter Egg Hints */}
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-primary mb-2 flex items-center space-x-2">
                    <Icon name="Lightbulb" size={16} />
                    <span>Pro Tips</span>
                  </h4>
                  <ul className="text-xs text-muted-foreground space-y-1 font-source">
                    <li>• Try querying: developer.skills.backend</li>
                    <li>• Type "help" in the console</li>
                    <li>• Look for the debug mode toggle</li>
                    <li>• Use arrow keys for command history</li>
                    <li>• Try: sudo make me a sandwich</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Footer Note */}
            <div className="mt-16 text-center">
              <div className="inline-block bg-muted/30 border border-border rounded-lg p-6 max-w-2xl">
                <Icon
                  name="Code"
                  size={24}
                  className="text-primary mx-auto mb-3"
                />
                <p className="text-muted-foreground mb-2">
                  This JSON resume is more than just data—it's a testament to
                  the journey from backend comfort zone to frontend adventure.
                </p>
                <p className="text-sm text-muted-foreground font-source">
                  <span className="text-warning">{"}"}</span> // End of
                  developer object (but not the learning journey)
                </p>
              </div>
            </div>
          </div>
        </main>
      )}

      <Footer />
    </div>
  );
};

export default AboutMe;
